import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RefreshCw,
  Download,
  Printer,
  Search,
  Calendar,
  Package,
  Loader2,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FixLineProfitDialog } from "@/components/reports/FixLineProfitDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DailyTotals = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({
    qty: 0,
    retail: 0,
    total: 0,
    profit: 0,
    discount: 0,
    item_count: 0,
    invoice_count: 0,
  });
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [fixLine, setFixLine] = useState(null);
  const { user } = useAuth();
  const canFixProfit = (user?.username || "").toLowerCase() === "herman";

  useEffect(() => {
    fetchDailyTotals();
  }, [selectedDate]);

  const fetchDailyTotals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales/daily-totals`, {
        params: { date: selectedDate },
      });
      setItems(response.data.items || []);
      setTotals(response.data.totals || {});
    } catch (error) {
      console.error("Error fetching daily totals:", error);
      toast.error("Failed to load daily totals");
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.description?.toLowerCase().includes(query) ||
      item.plu?.toLowerCase().includes(query) ||
      item.inv_no?.toLowerCase().includes(query) ||
      item.user?.toLowerCase().includes(query) ||
      item.customer?.toLowerCase().includes(query)
    );
  });

  const handleExportCSV = () => {
    if (items.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = [
      "InvNo",
      "Date",
      "PLU",
      "Description",
      "Qty",
      "Retail",
      "Total",
      "InvTotal",
      "Profit",
      "User",
      "Terminal",
      "DiscAmt",
      "Customer",
    ];
    const csvContent = [
      headers.join(","),
      ...items.map((item) =>
        [
          item.inv_no,
          item.date,
          item.plu,
          `"${item.description}"`,
          item.qty,
          item.retail.toFixed(2),
          item.total.toFixed(2),
          item.inv_total.toFixed(2),
          item.profit.toFixed(2),
          item.user,
          item.terminal,
          item.disc_amt.toFixed(2),
          `"${item.customer}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `daily_totals_${selectedDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export completed");
  };

  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <title>Daily Totals - ${selectedDate}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 10px; }
            h1 { font-size: 16px; margin-bottom: 5px; }
            h2 { font-size: 12px; color: #666; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 4px; text-align: left; }
            th { background: #f5f5f5; font-weight: bold; }
            .number { text-align: right; }
            .total-row { font-weight: bold; background: #f0f0f0; }
            .summary { margin-top: 20px; display: flex; gap: 20px; flex-wrap: wrap; }
            .summary-box { border: 1px solid #ddd; padding: 10px; min-width: 100px; }
            .summary-label { font-size: 10px; color: #666; }
            .summary-value { font-size: 14px; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>DAILY TOTALS - BOSVELD BOU & HARDEWARE</h1>
          <h2>Date: ${selectedDate} | Items: ${totals.item_count} | Invoices: ${totals.invoice_count}</h2>
          
          <table>
            <thead>
              <tr>
                <th>InvNo</th>
                <th>Date</th>
                <th>PLU</th>
                <th>Description</th>
                <th class="number">Qty</th>
                <th class="number">Retail</th>
                <th class="number">Total</th>
                <th class="number">InvTotal</th>
                <th class="number">Profit</th>
                <th>User</th>
                <th>Terminal</th>
                <th class="number">DiscAmt</th>
              </tr>
            </thead>
            <tbody>
              ${items
                .map(
                  (item) => `
                <tr>
                  <td>${item.inv_no}</td>
                  <td>${item.date}</td>
                  <td>${item.plu}</td>
                  <td>${item.description}</td>
                  <td class="number">${item.qty}</td>
                  <td class="number">R ${item.retail.toFixed(2)}</td>
                  <td class="number">R ${item.total.toFixed(2)}</td>
                  <td class="number">R ${item.inv_total.toFixed(2)}</td>
                  <td class="number">R ${item.profit.toFixed(2)}</td>
                  <td>${item.user}</td>
                  <td>${item.terminal}</td>
                  <td class="number">R ${item.disc_amt.toFixed(2)}</td>
                </tr>
              `
                )
                .join("")}
              <tr class="total-row">
                <td colspan="4">TOTALS</td>
                <td class="number">${totals.qty}</td>
                <td class="number">R ${totals.retail?.toFixed(2)}</td>
                <td class="number">R ${totals.total?.toFixed(2)}</td>
                <td></td>
                <td class="number">R ${totals.profit?.toFixed(2)}</td>
                <td colspan="2"></td>
                <td class="number">R ${totals.discount?.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="h-6 w-6 text-purple-600" />
            Daily Totals
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            All items sold - line by line detail
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Date:</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDailyTotals}
            disabled={loading}
          >
            <RefreshCw
              className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-4 mb-6">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Receipt className="h-4 w-4" />
              <span className="text-xs font-medium">Invoices</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">
              {totals.invoice_count || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <Package className="h-4 w-4" />
              <span className="text-xs font-medium">Items Sold</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">
              {totals.item_count || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <ShoppingCart className="h-4 w-4" />
              <span className="text-xs font-medium">Total Qty</span>
            </div>
            <p className="text-2xl font-bold text-green-700">
              {totals.qty || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-orange-600 mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs font-medium">Net Daily Total</span>
            </div>
            <p className="text-2xl font-bold text-orange-700">
              R {(totals.net_total !== undefined ? totals.net_total : totals.total || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}
            </p>
            {totals.refunds_total > 0 || totals.petty_cash_total > 0 ? (
              <p className="text-xs text-orange-600 mt-1">
                (Sales - Refunds - Petty Cash)
              </p>
            ) : null}
          </CardContent>
        </Card>
        <Card className="bg-teal-50 border-teal-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-teal-600 mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs font-medium">Total Profit</span>
            </div>
            <p className="text-2xl font-bold text-teal-700">
              R {(totals.profit || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs font-medium">Discounts</span>
            </div>
            <p className="text-2xl font-bold text-red-700">
              R {(totals.discount || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by description, PLU, invoice, user..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Package className="h-12 w-12 mb-2 opacity-50" />
              <p>No sales found for {selectedDate}</p>
            </div>
          ) : (
            <div className="max-h-[calc(100vh-400px)] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-gray-50">
                  <TableRow>
                    <TableHead>InvNo</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>PLU</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Retail</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">InvTotal</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Terminal</TableHead>
                    <TableHead className="text-right">DiscAmt</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item, idx) => (
                    <TableRow key={idx} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-blue-600">
                        {item.inv_no}
                      </TableCell>
                      <TableCell>{item.date}</TableCell>
                      <TableCell className="text-gray-500 text-xs">
                        {item.plu}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={item.description}>
                        {item.description}
                      </TableCell>
                      <TableCell className="text-right">{item.qty}</TableCell>
                      <TableCell className="text-right">
                        R {item.retail.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        R {item.total.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-gray-500">
                        R {item.inv_total.toFixed(2)}
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium ${
                          item.profit >= 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {canFixProfit && item.profit_editable && item.sale_id ? (
                          <button type="button" onClick={() => setFixLine(item)} title="Click to fix this line's profit (password required)"
                            className="underline decoration-dotted underline-offset-2 hover:opacity-70" data-testid={`fix-profit-btn-${item.inv_no}-${item.line_index}`}>
                            R {item.profit.toFixed(2)}{item.profit_overridden ? " *" : ""}
                          </button>
                        ) : (
                          <>R {item.profit.toFixed(2)}{item.profit_overridden ? " *" : ""}</>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{item.user}</TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {item.terminal}
                      </TableCell>
                      <TableCell className="text-right text-red-500">
                        {item.disc_amt > 0 ? `R ${item.disc_amt.toFixed(2)}` : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  <TableRow className="bg-gray-100 font-bold sticky bottom-0">
                    <TableCell colSpan={4}>
                      TOTALS ({filteredItems.length} items)
                    </TableCell>
                    <TableCell className="text-right">{totals.qty}</TableCell>
                    <TableCell className="text-right">
                      R {(totals.retail || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      R {(totals.total || 0).toFixed(2)}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right text-green-600">
                      R {(totals.profit || 0).toFixed(2)}
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                    <TableCell className="text-right text-red-500">
                      R {(totals.discount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <FixLineProfitDialog line={fixLine} onClose={() => setFixLine(null)} onSaved={fetchDailyTotals} />
    </div>
  );
};

export default DailyTotals;
